import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
} from 'date-fns';
import { getUserList } from '../../../axios/User/UserApi';
import { getListGroup } from '../../../axios/Group/GroupUrlApi';
import LeaveApi from '../../../axios/LeaveAndHoliday/LeaveApi';
import CustomSpinner from '../../../components/CustomSpinner';
import { LoadingProvider } from '../../../context/LoadingContext';
import { useRefresh } from '../../../context/RefreshContext';
import LeaveAndHolidayInfo from './LeaveAndHolidayInfo';
import LeaveAndHolidayTabs from './LeaveAndHolidayTabs';

const LeaveAndHoliday = () => {
  const [tabValue, setTabValue] = useState(0);
  const { getLeaveTypeData } = LeaveApi();
  const [selectedHolidayRows, setSelectedHolidayRows] = useState({});
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const location = useLocation();
  const { myData } = location.state || {};
  const dialogRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(
    myData && !isNaN(new Date(myData).getTime()) ? new Date(myData) : new Date()
  );

  const { refreshData, handleRefreshData } = useRefresh();
  const [holidayPage, setHolidayPage] = useState(0);
  const [holidayPageSize, setHolidayPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [countData, ] = useState(0);
  const [dispalyWeek, setDispalyWeek] = useState(0);
  const [userData, setUserData] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const [leaveTypesData, setLeaveTypesData] = useState([]);
  const [formatLeaveFilter, setFormatLeaveFilter] = useState({});

  const handleGetDataFilterLeave = useCallback((data) => {
    setFormatLeaveFilter(data);
  }, []);

  const handleHolidayRowSelection = (newSelection) => {
    setSelectedHolidayRows(newSelection);
  };

  const fetchUserData = async () => {
    try {
      const payload = {
        statusId: 1,
        page: 1,
        pageSize: 500,
        status: null,
        langId: localStorage.getItem('i18nextLngId'),
      };
      const response = await getUserList(payload);
      if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
        setUserData(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.message || t('common.network-timeout'));
      }
    }
  };

  const fetchGroupData = async () => {
    const payload = {
      pageNo: 1,
      recordPerPage: 500,
      langId: localStorage.getItem('i18nextLngId'),
    };
    try {
      const response = await getListGroup(payload);
      if (response.data.data?.length > 0) {
        setGroupData(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(t(error?.response?.data) || t('common.network-timeout'));
      }
    }
  };

  const fetchLeaveTypeData = async () => {
    const payload = {
      langId: localStorage.getItem('i18nextLngId'),
      isDeleted: true,
    };
    try {
      const response = await getLeaveTypeData(payload);
      if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
        setLeaveTypesData(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.message || t('common.network-timeout'));
      }
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchGroupData();
  }, []);

  useEffect(() => {
    fetchLeaveTypeData();
  }, [refreshData]);

  const calculateDisplayedWeeks = (date, weekStartsOnAbbr) => {
    let weekDayAbbrToIndex = {};
    if (currentLang === 'vi') {
      weekDayAbbrToIndex = { CN: 0, T2: 1, T3: 2, T4: 3, T5: 4, T6: 5, T7: 6 };
    } else {
      weekDayAbbrToIndex = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    }

    const weekStartsOn = weekDayAbbrToIndex[weekStartsOnAbbr];
    if (weekStartsOn === undefined) {
      throw new Error("Invalid week start day. Use 'Sun', 'Mon', 'Tue', etc.");
    }

    const firstDayOfMonth = startOfMonth(date);
    const lastDayOfMonth = endOfMonth(date);
    const calendarStartDate = startOfWeek(firstDayOfMonth, { weekStartsOn });
    const daysInGrid = eachDayOfInterval({ start: calendarStartDate, end: lastDayOfMonth });
    const totalCells = daysInGrid.length <= 35 ? 35 : 42;
    setDispalyWeek(totalCells / 7);
    return totalCells / 7;
  };

  return (
    <div className="leave-and-holiday">
      <div className="card min-w-0">
        <LoadingProvider>
          <LeaveAndHolidayTabs
            tabValue={tabValue}
            setTabValue={setTabValue}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            refreshData={refreshData}
            formatLeaveFilter={formatLeaveFilter}
            onFilterChange={handleGetDataFilterLeave}
            groupData={groupData}
            dispalyWeek={dispalyWeek}
            countData={countData}
            leaveTypesData={leaveTypesData}
            dialogRef={dialogRef}
            calculateDisplayedWeeks={calculateDisplayedWeeks}
            setLoading={setLoading}
            holidayPage={holidayPage}
            holidayPageSize={holidayPageSize}
            setHolidayPage={setHolidayPage}
            setHolidayPageSize={setHolidayPageSize}
            onHolidayRowSelection={handleHolidayRowSelection}
          />
        </LoadingProvider>
      </div>

      <LeaveAndHolidayInfo
        ref={dialogRef}
        dialogOnly
        selectedDate={selectedDate}
        onRefresh={handleRefreshData}
        refreshData={refreshData}
        setLoading={setLoading}
        holidayInfo={selectedHolidayRows}
        userData={userData}
        groupData={groupData}
        leaveTypesData={leaveTypesData}
        formatLeaveFilter={formatLeaveFilter}
        setTabValue={setTabValue}
      />

      {loading && <CustomSpinner loading={loading} />}
    </div>
  );
};

export default LeaveAndHoliday;
